import { useEffect, useState } from "react";
import { Drawer, Button } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getConsentTemplates,
  createPatientConsent,
  openPatientConsentPdf,
} from "../../apis/consent";
import { ConsentTemplate, ConsentTemplateField } from "../../interfaces/Consent";
import { useNotification } from "../../NotificationContext";

const inputCls =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

const selectCls =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: number;
  visitId?: number | null;
  /** Pre-filled values keyed by template field key */
  prefill?: Record<string, string>;
}

const ConsentGeneratorDrawer = ({ open, onClose, patientId, visitId, prefill }: Props) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [templates, setTemplates]               = useState<ConsentTemplate[]>([]);
  const [part2Template, setPart2Template]       = useState<ConsentTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ConsentTemplate | null>(null);
  const [filledData, setFilledData]             = useState<Record<string, string>>({});
  const [notes, setNotes]                       = useState("");
  const [saving, setSaving]                     = useState(false);

  useEffect(() => {
    if (!open) return;
    getConsentTemplates()
      .then((res: any) => {
        const list: ConsentTemplate[] = res.data ?? res;
        setTemplates(list);
        setPart2Template(list.find((t) => t.key === "part2_undertaking") ?? null);
      })
      .catch(() => {});
  }, [open]);

  const handleClose = () => {
    setSelectedTemplate(null);
    setFilledData({});
    setNotes("");
    onClose();
  };

  const applyPrefill = (fields: ConsentTemplateField[]) => {
    if (!prefill) return {};
    const seeded: Record<string, string> = {};
    for (const f of fields) {
      if (prefill[f.fieldKey] != null && prefill[f.fieldKey] !== "") {
        seeded[f.fieldKey] = prefill[f.fieldKey];
      }
    }
    return seeded;
  };

  const handleTemplateChange = (templateId: number) => {
    const tpl = templates.find((t) => t.id === templateId) ?? null;
    setSelectedTemplate(tpl);

    if (tpl) {
      const allFields = [
        ...tpl.fields,
        ...(tpl.pattern === "leaflet" && part2Template
          ? part2Template.fields.filter((f) => f.fieldKey !== "procedure_name")
          : []),
      ];
      setFilledData(applyPrefill(allFields));
    } else {
      setFilledData({});
    }
  };

  const handleField = (key: string, value: string) =>
    setFilledData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!selectedTemplate) {
      showNotification("error", "Please select a consent template", "", "topRight");
      return;
    }
    setSaving(true);
    try {
      const res: any = await createPatientConsent({
        patientId,
        visitId: visitId || null,
        templateId: selectedTemplate.id,
        filledData,
        notes,
      });
      const consent = res.data ?? res;
      showNotification("success", `Consent ${consent.reference} created`, "", "topRight");

      try {
        await openPatientConsentPdf(consent.id);
      } catch {
        showNotification("error", "Consent saved but PDF generation failed", "", "topRight");
      }

      handleClose();
      navigate(`/patient-consents/${consent.id}`);
    } catch {
      showNotification("error", "Failed to create consent", "", "topRight");
    } finally {
      setSaving(false);
    }
  };

  const ownFields: ConsentTemplateField[] = selectedTemplate?.fields ?? [];
  const part2Fields: ConsentTemplateField[] =
    selectedTemplate?.pattern === "leaflet" && part2Template
      ? part2Template.fields.filter((f) => f.fieldKey !== "procedure_name")
      : [];

  return (
    <Drawer
      title="Generate Consent"
      open={open}
      onClose={handleClose}
      width={520}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSubmit}>
            Generate & Open PDF
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1.5">
            Consent Template <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedTemplate?.id ?? ""}
            onChange={(e) => handleTemplateChange(Number(e.target.value))}
            className={selectCls}
          >
            <option value="">Select template...</option>
            {templates
              .filter((t) => t.pattern !== "shared_part2")
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </div>

        {(ownFields.length > 0 || part2Fields.length > 0) && (
          <div className="rounded-md border p-4 space-y-5">
            {selectedTemplate?.pattern === "leaflet" ? (
              <>
                {ownFields.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                      Part I — Leaflet
                    </p>
                    <div className="space-y-4">
                      {ownFields.map((f) => (
                        <FieldRow
                          key={f.fieldKey}
                          field={f}
                          value={filledData[f.fieldKey] ?? ""}
                          prefilled={!!prefill?.[f.fieldKey]}
                          onChange={handleField}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {part2Fields.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
                      Part II — Undertaking
                    </p>
                    <div className="space-y-4">
                      {part2Fields.map((f) => (
                        <FieldRow
                          key={f.fieldKey}
                          field={f}
                          value={filledData[f.fieldKey] ?? ""}
                          prefilled={!!prefill?.[f.fieldKey]}
                          onChange={handleField}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-4">
                {ownFields.map((f) => (
                  <FieldRow
                    key={f.fieldKey}
                    field={f}
                    value={filledData[f.fieldKey] ?? ""}
                    prefilled={!!prefill?.[f.fieldKey]}
                    onChange={handleField}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900 mb-1.5">
            Notes <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
    </Drawer>
  );
};

const FieldRow = ({
  field,
  value,
  prefilled,
  onChange,
}: {
  field: ConsentTemplateField;
  value: string;
  prefilled: boolean;
  onChange: (key: string, value: string) => void;
}) => {
  const cls =
    "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <label className="block text-sm font-medium leading-6 text-gray-900">
          {field.label ?? field.fieldKey}
        </label>
        {prefilled && value !== "" && (
          <span className="text-xs text-indigo-500 font-normal">auto-filled</span>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field.fieldKey, e.target.value)}
        className={cls}
      />
    </div>
  );
};

export default ConsentGeneratorDrawer;
